// src/router/ProtectedRoute.jsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { canAccessPage, landingPageForRole } from '../utils/rolePermissions';

/**
 * Gates a route on authentication and, when `page` is given, on the account
 * role being allowed to open that page.
 *
 * An unauthenticated visitor goes to the login screen. An authenticated one
 * who may not open this page goes to their own landing page rather than the
 * login screen, so a staff account following an admin's bookmark lands
 * somewhere useful instead of looking logged out.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.page] Page key from `utils/rolePermissions`. Omit for
 *   routes every authenticated account may open.
 * @param {string} [props.basePath] Route prefix these routes are mounted under
 *   ('' or '/sync-monitor'), so a redirect stays inside the same prefix.
 */
const ProtectedRoute = ({ children, page, basePath = '' }) => {
  const { isAuthenticated, role } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to={`${basePath}/login`} replace />;
  }

  if (page !== undefined && !canAccessPage(role, page)) {
    const landingPage = landingPageForRole(role);
    return <Navigate to={`${basePath}/${landingPage}`} replace />;
  }

  return children;
};

export default ProtectedRoute;
